import React, { useState, useEffect } from 'react';
import api from '../api';
import config from '../config';
import { FaTimes, FaChevronLeft, FaChevronRight, FaImage } from 'react-icons/fa';

const Gallery = () => {
  // --- States ---
  const [galleryImages, setGalleryImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [activeCategory, setActiveCategory] = useState('All');

  // --- Fixed Categories (UI Design ke liye) ---
  const categories = ['All', 'Residential', 'Commercial', 'Institutional', 'Society', 'Events', 'Team'];

  // --- 1. Fetch Images from Backend ---
  useEffect(() => {
    const fetchImages = async () => {
      try {
    const response = await api.get('/Gallery');
        
        // Data format map karein taaki frontend logic ke sath match kare
        const formattedData = response.data.map(img => ({
          id: img.id,
          // Agar URL relative hai to Base URL jodein
         src: img.imageUrl.startsWith('http') ? img.imageUrl : `${config.IMG_BASE_URL}${img.imageUrl}`,
          title: img.title,
          category: img.category,
          location: img.location
        }));

        setGalleryImages(formattedData);
      } catch (error) {
        console.error("Error fetching gallery images:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchImages();
  }, []);

  // --- Filter Logic ---
  const filteredImages = activeCategory === 'All' 
    ? galleryImages 
    : galleryImages.filter(img => img.category === activeCategory);

  // --- Lightbox Functions ---
  const openLightbox = (index) => {
    setCurrentIndex(index);
    setSelectedImage(filteredImages[index]);
  };

  const closeLightbox = () => {
    setSelectedImage(null);
  };

  const goToPrevious = () => {
    const newIndex = currentIndex === 0 ? filteredImages.length - 1 : currentIndex - 1;
    setCurrentIndex(newIndex);
    setSelectedImage(filteredImages[newIndex]);
  };

  const goToNext = () => {
    const newIndex = currentIndex === filteredImages.length - 1 ? 0 : currentIndex + 1;
    setCurrentIndex(newIndex);
    setSelectedImage(filteredImages[newIndex]);
  };

  return (
    <section className="gallery-section py-5 bg-light" id="gallery">
      <div className="container">
        {/* Section Header */}
        <div className="text-center mb-5">
          <span className="badge bg-primary mb-3 px-3 py-2">
            <FaImage className="me-2" />
            Our Work Gallery
          </span>
          <h2 className="display-5 fw-bold text-dark mb-3">
            Our Solar Installations Across Bihar & Jharkhand
          </h2>
          <p className="lead text-muted">
            Real projects, real impact - See our work in action
          </p>
        </div>

        {/* Category Filter */}
        <div className="category-filter mb-5 text-center">
          <div className="btn-group flex-wrap" role="group">
            {categories.map((category, index) => (
              <button
                key={index}
                type="button"
                className={`btn ${activeCategory === category ? 'btn-success' : 'btn-outline-success'} rounded-pill mx-1 mb-2`}
                onClick={() => setActiveCategory(category)}
                style={{ minWidth: '100px' }}
              >
                {category}
              </button>
            ))}
          </div>
        </div>

        {/* Loading State */}
        {loading ? (
          <div className="text-center py-5">
            <div className="spinner-border text-success" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-2 text-muted">Loading projects...</p>
          </div>
        ) : (
          /* Gallery Grid */
          <div className="row g-4">
            {filteredImages.length > 0 ? (
              filteredImages.map((image, index) => (
                <div className="col-lg-4 col-md-6" key={image.id}>
                  <div 
                    className="gallery-item card border-0 shadow-sm overflow-hidden h-100"
                    style={{
                      cursor: 'pointer',
                      transition: 'all 0.3s ease'
                    }}
                    onClick={() => openLightbox(index)}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-10px)';
                      e.currentTarget.style.boxShadow = '0 20px 50px rgba(0, 0, 0, 0.2)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.1)';
                    }}
                  >
                    {/* Image */}
                    <div 
                      className="gallery-image position-relative"
                      style={{
                        height: '300px',
                        overflow: 'hidden'
                      }}
                    >
                      <img 
                        src={image.src} 
                        alt={image.title}
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                          transition: 'transform 0.3s ease'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.transform = 'scale(1.1)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = 'scale(1)';
                        }}
                        // Agar image load fail ho jaye to placeholder dikhayein
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = "https://via.placeholder.com/400x300?text=Image+Not+Found";
                        }}
                      />
                      
                      {/* Overlay */}
                      <div 
                        className="gallery-overlay position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
                        style={{
                          background: 'linear-gradient(135deg, rgba(27, 63, 110, 0.9) 0%, rgba(0, 208, 132, 0.9) 100%)',
                          opacity: 0,
                          transition: 'opacity 0.3s ease'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.opacity = 1;
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.opacity = 0;
                        }}
                      >
                        <div className="text-center text-white">
                          <FaImage style={{ fontSize: '3rem', marginBottom: '10px' }} />
                          <p className="mb-0 fw-bold">View Details</p>
                        </div>
                      </div>
                    </div>

                    {/* Card Body */}
                    <div className="card-body p-3">
                      <span className="badge bg-success mb-2">{image.category}</span>
                      <h5 className="fw-bold mb-2" style={{ color: '#1B3F6E', fontSize: '1rem' }}>
                        {image.title}
                      </h5>
                      <p className="text-muted small mb-0">
                        <i className="bi bi-geo-alt-fill me-1" style={{ color: '#00D084' }}></i>
                        {image.location}
                      </p>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              /* Empty State */
              <div className="text-center py-5 w-100">
                <FaImage style={{ fontSize: '5rem', color: '#E0E0E0' }} />
                <p className="text-muted mt-3">No images found for "{activeCategory}"</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Lightbox Modal */}
      {selectedImage && (
        <div 
          className="lightbox-modal position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
          style={{
            background: 'rgba(0, 0, 0, 0.95)',
            zIndex: 9999,
            animation: 'fadeIn 0.3s ease'
          }}
          onClick={closeLightbox}
        >
          {/* Close Button */}
          <button
            className="btn btn-light rounded-circle position-absolute"
            style={{
              top: '20px',
              right: '20px',
              width: '50px',
              height: '50px',
              zIndex: 10000
            }}
            onClick={closeLightbox}
          >
            <FaTimes style={{ fontSize: '1.5rem' }} />
          </button>

          {/* Previous Button */}
          <button
            className="btn btn-light rounded-circle position-absolute"
            style={{
              left: '20px',
              width: '50px',
              height: '50px',
              zIndex: 10000
            }}
            onClick={(e) => {
              e.stopPropagation();
              goToPrevious();
            }}
          >
            <FaChevronLeft style={{ fontSize: '1.5rem' }} />
          </button>

          {/* Image Container */}
          <div 
            className="lightbox-content"
            onClick={(e) => e.stopPropagation()}
            style={{
              maxWidth: '90%',
              maxHeight: '90vh'
            }}
          >
            <img 
              src={selectedImage.src} 
              alt={selectedImage.title}
              style={{
                maxWidth: '100%',
                maxHeight: '80vh',
                objectFit: 'contain',
                borderRadius: '10px'
              }}
            />
            <div className="text-center mt-3 text-white">
              <h4 className="fw-bold">{selectedImage.title}</h4>
              <p className="mb-0">
                <span className="badge bg-success me-2">{selectedImage.category}</span>
                <i className="bi bi-geo-alt-fill me-1"></i>
                {selectedImage.location}
              </p>
              <p className="small text-white-50 mt-2">
                Image {currentIndex + 1} of {filteredImages.length}
              </p>
            </div>
          </div>

          {/* Next Button */}
          <button
            className="btn btn-light rounded-circle position-absolute"
            style={{
              right: '20px',
              width: '50px',
              height: '50px',
              zIndex: 10000
            }}
            onClick={(e) => {
              e.stopPropagation();
              goToNext();
            }}
          >
            <FaChevronRight style={{ fontSize: '1.5rem' }} />
          </button>
        </div>
      )}

      {/* Custom Animations */}
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
      `}</style>
    </section>
  );
};

export default Gallery;